import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActionIcon, Alert, Button, Group, Loader, Popover, Select, Stack, Text } from '@mantine/core';
import { IconAlertCircle, IconChevronDown, IconPlayerPlay, IconPlayerStop } from '@tabler/icons-react';
import { useSubscription } from 'urql';
import {
  buildFlowVoiceCatalogKey,
  flowVoiceCatalogVoiceFromConfig,
  flowVoiceConfigFromCatalogVoice,
  getFlowVoiceProviderLabel,
} from '@legacy-shared/widgets/core/voiceCatalog';
import type { FlowVoiceCatalogVoice, FlowVoiceProviderId } from '@legacy-shared/widgets/core/voiceCatalog';
import { currentFlowVoicePresets, legacyFlowVoicePresets } from '@legacy-shared/widgets/core/voicePresets';
import type { FlowVoiceConfig } from '@legacy-shared/widgets/core/types';
import { api } from '@/lib/api';
import { getErrorMessage } from '@/lib/error-message';
import { ElevenLabsVoicesSubscription } from './graphql';

const languageOptions = [
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'es-ES', label: 'Spanish (Spain)' },
  { value: 'es-MX', label: 'Spanish (Mexico)' },
  { value: 'fr-FR', label: 'French' },
  { value: 'de-DE', label: 'German' },
];

const providerOrder: FlowVoiceProviderId[] = [
  'telnyx',
  'aws',
  'azure',
  'elevenlabs',
  'minimax',
  'rime',
  'resemble',
  'inworld',
  'legacy',
  'custom',
];

const baseUserSelectableProviders: FlowVoiceProviderId[] = [
  'telnyx',
  'aws',
  'azure',
  'elevenlabs',
  'minimax',
  'rime',
  'resemble',
  'inworld',
];

const nestedSelectComboboxProps = { withinPortal: false, position: 'bottom-start' as const };

type ElevenLabsVoice = {
  voiceId: string;
  name: string;
  gender: string | null;
  accent: string | null;
  previewUrl: string;
  recommendedModelId: string;
};

function voiceMatchesLanguage(voice: FlowVoiceCatalogVoice, languageCode: string) {
  const target = languageCode.toLowerCase();
  const localePrefix = voice.locale?.split('-')[0]?.toLowerCase();
  const langPrefix = voice.language?.split('-')[0]?.toLowerCase();

  if (!localePrefix && !langPrefix) return true;

  return localePrefix === target || langPrefix === target;
}

function fallbackCatalogVoices() {
  return [...currentFlowVoicePresets, ...legacyFlowVoicePresets].map((p) => flowVoiceCatalogVoiceFromConfig(p.config));
}

function elevenLabsCatalogVoices(voices: ElevenLabsVoice[]): FlowVoiceCatalogVoice[] {
  return voices.map((voice) => ({
    key: buildFlowVoiceCatalogKey('elevenlabs', voice.voiceId, voice.recommendedModelId),
    provider: 'elevenlabs',
    providerLabel: getFlowVoiceProviderLabel('elevenlabs'),
    voiceIdentifier: `ElevenLabs.${voice.recommendedModelId}.${voice.voiceId}`,
    voiceId: voice.voiceId,
    voiceName: voice.name,
    modelId: voice.recommendedModelId,
    modelLabel: voice.recommendedModelId,
    locale: voice.accent ?? undefined,
    gender: voice.gender ?? undefined,
    previewUrl: voice.previewUrl,
  }));
}

function voiceLabel(voice: FlowVoiceCatalogVoice) {
  const details = [voice.locale ?? voice.language, voice.gender].filter(Boolean).join(', ');

  return details ? `${voice.voiceName} (${details})` : voice.voiceName;
}

function providerLabel(voice: FlowVoiceCatalogVoice) {
  if (voice.provider === 'legacy') return `Legacy: ${voice.voiceName}`;
  if (voice.provider === 'custom') return `Custom: ${voice.voiceName}`;

  return `${voice.providerLabel}: ${voice.voiceName}`;
}

function buildModelOptions(voices: FlowVoiceCatalogVoice[]) {
  const models = new Map(voices.flatMap((v) => (v.modelId ? [[v.modelId, v.modelLabel ?? v.modelId]] : [])));

  return [...models.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((left, right) => left.label.localeCompare(right.label));
}

function usePreviewAudio(previewUrl: string | undefined) {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(
    () => () => {
      audioRef.current?.pause();
      audioRef.current = null;
    },
    [],
  );

  const toggle = useCallback(() => {
    if (!previewUrl) return;

    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);

      return;
    }

    audioRef.current?.pause();

    const audio = new Audio(previewUrl);

    audioRef.current = audio;
    audio.onended = () => setIsPlaying(false);
    audio.onerror = () => setIsPlaying(false);
    void audio.play();
    setIsPlaying(true);
  }, [previewUrl, isPlaying]);

  return { isPlaying, toggle };
}

function useTelnyxVoiceCatalog() {
  const [voices, setVoices] = useState<FlowVoiceCatalogVoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    void api.voices.telnyx.get().then((result) => {
      if (cancelled) return;

      if (result.error) {
        setError(getErrorMessage(result.error, 'Failed to load Telnyx voice catalog'));
        setVoices([]);
      } else {
        setError(null);
        setVoices(result.data);
      }

      setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return { voices, isLoading, error };
}

type FlowVoiceSelectorProps = {
  voiceConfig: FlowVoiceConfig;
  onChange: (config: FlowVoiceConfig) => void;
  elevenLabsAvailable: boolean;
  languageFilter?: string;
};

export function FlowVoiceSelector({
  voiceConfig,
  onChange,
  elevenLabsAvailable,
  languageFilter,
}: FlowVoiceSelectorProps) {
  const catalog = useTelnyxVoiceCatalog();

  const [{ data: elevenLabsData }] = useSubscription({
    query: ElevenLabsVoicesSubscription,
    pause: !elevenLabsAvailable,
  });

  const [opened, setOpened] = useState(false);
  const [draftProvider, setDraftProvider] = useState<FlowVoiceProviderId | null>(null);
  const [draftModelId, setDraftModelId] = useState<string | null>(null);
  const [draftVoiceKey, setDraftVoiceKey] = useState<string | null>(null);

  const userSelectableProviders = useMemo(
    () => new Set(baseUserSelectableProviders.filter((p) => p !== 'elevenlabs' || elevenLabsAvailable)),
    [elevenLabsAvailable],
  );

  const fallbackVoices = useMemo(() => fallbackCatalogVoices(), []);
  const premiumVoices = useMemo(
    () => elevenLabsCatalogVoices(elevenLabsData?.elevenLabsVoices ?? []),
    [elevenLabsData],
  );

  const mergedCatalogVoices = useMemo(() => {
    const combined = new Map<string, FlowVoiceCatalogVoice>();
    const currentSelection = flowVoiceCatalogVoiceFromConfig(voiceConfig);

    for (const voice of catalog.voices.length > 0 ? catalog.voices : fallbackVoices) {
      if (!elevenLabsAvailable && voice.provider === 'elevenlabs' && voice.key !== currentSelection.key) continue;

      combined.set(voice.key, voice);
    }

    if (elevenLabsAvailable) {
      for (const voice of premiumVoices) combined.set(voice.key, voice);
    }

    for (const voice of fallbackVoices.filter((v) => v.provider === 'legacy')) {
      if (!combined.has(voice.key)) combined.set(voice.key, voice);
    }

    if (!combined.has(currentSelection.key)) combined.set(currentSelection.key, currentSelection);

    return [...combined.values()].sort((left, right) => {
      const providerDiff = providerOrder.indexOf(left.provider) - providerOrder.indexOf(right.provider);

      if (providerDiff !== 0) return providerDiff;

      const modelDiff = (left.modelLabel ?? '').localeCompare(right.modelLabel ?? '');

      return modelDiff !== 0 ? modelDiff : left.voiceName.localeCompare(right.voiceName);
    });
  }, [catalog.voices, elevenLabsAvailable, premiumVoices, fallbackVoices, voiceConfig]);

  const catalogByKey = useMemo(() => new Map(mergedCatalogVoices.map((v) => [v.key, v])), [mergedCatalogVoices]);

  const selectedVoice = useMemo(() => {
    const fallback = flowVoiceCatalogVoiceFromConfig(voiceConfig);
    const exact = catalogByKey.get(fallback.key);

    if (exact) return exact;

    const compatible = mergedCatalogVoices.find((voice) =>
      fallback.provider === 'elevenlabs'
        ? voice.provider === 'elevenlabs' && voice.voiceId === fallback.voiceId
        : voice.voiceIdentifier === fallback.voiceIdentifier,
    );

    return compatible ?? fallback;
  }, [catalogByKey, mergedCatalogVoices, voiceConfig]);

  const clearDraftSelection = useCallback(() => {
    setDraftProvider(null);
    setDraftModelId(null);
    setDraftVoiceKey(null);
  }, []);

  const displayCatalogVoices = useMemo(
    () =>
      languageFilter
        ? mergedCatalogVoices.filter((v) => v.key === selectedVoice.key || voiceMatchesLanguage(v, languageFilter))
        : mergedCatalogVoices,
    [languageFilter, mergedCatalogVoices, selectedVoice.key],
  );

  const providerOptions = useMemo(() => {
    const providers = new Set(displayCatalogVoices.map((v) => v.provider));

    if (elevenLabsAvailable && !languageFilter) providers.add('elevenlabs');

    providers.add(selectedVoice.provider);

    return [...providers]
      .filter((p) => userSelectableProviders.has(p) || p === selectedVoice.provider)
      .sort((left, right) => providerOrder.indexOf(left) - providerOrder.indexOf(right))
      .map((p) => ({ value: p, label: getFlowVoiceProviderLabel(p) }));
  }, [elevenLabsAvailable, displayCatalogVoices, languageFilter, selectedVoice.provider, userSelectableProviders]);

  const activeProvider = draftProvider ?? selectedVoice.provider;

  const providerVoices = useMemo(
    () => displayCatalogVoices.filter((v) => v.provider === activeProvider),
    [activeProvider, displayCatalogVoices],
  );

  const modelOptions = useMemo(() => buildModelOptions(providerVoices), [providerVoices]);

  const preferredModelId = draftModelId ?? selectedVoice.modelId ?? null;

  const activeModelId =
    preferredModelId && modelOptions.some((o) => o.value === preferredModelId)
      ? preferredModelId
      : (modelOptions[0]?.value ?? null);

  const visibleVoices = useMemo(
    () =>
      modelOptions.length === 0 || !activeModelId
        ? providerVoices
        : providerVoices.filter((v) => v.modelId === activeModelId),
    [activeModelId, modelOptions.length, providerVoices],
  );

  const activeVoice = useMemo(() => {
    if (visibleVoices.length === 0) return undefined;

    const draftVoice = draftVoiceKey ? catalogByKey.get(draftVoiceKey) : undefined;

    if (draftVoice && visibleVoices.some((v) => v.key === draftVoice.key)) return draftVoice;
    if (visibleVoices.some((v) => v.key === selectedVoice.key)) return selectedVoice;

    return visibleVoices[0] ?? selectedVoice;
  }, [catalogByKey, draftVoiceKey, selectedVoice, visibleVoices]);

  const preview = usePreviewAudio(activeVoice?.previewUrl);

  const handleProviderChange = useCallback(
    (value: string | null) => {
      if (!value) return;

      const nextVoices = displayCatalogVoices.filter((v) => v.provider === value);
      const nextModelOptions = buildModelOptions(nextVoices);
      const nextModelId = nextModelOptions[0]?.value ?? null;
      const nextVisible =
        nextModelOptions.length > 0 && nextModelId ? nextVoices.filter((v) => v.modelId === nextModelId) : nextVoices;

      setDraftProvider(value as FlowVoiceProviderId);
      setDraftModelId(nextModelId);
      setDraftVoiceKey(nextVisible[0]?.key ?? null);
    },
    [displayCatalogVoices],
  );

  const handleModelChange = useCallback(
    (value: string | null) => {
      if (!value) return;

      setDraftModelId(value);
      setDraftVoiceKey(providerVoices.find((v) => v.modelId === value)?.key ?? null);
    },
    [providerVoices],
  );

  const handleVoiceChange = useCallback(
    (value: string | null) => {
      const voice = value ? catalogByKey.get(value) : undefined;

      if (!voice) return;

      setDraftVoiceKey(voice.key);
      onChange(flowVoiceConfigFromCatalogVoice(voice, voiceConfig));
      setOpened(false);
    },
    [catalogByKey, onChange, voiceConfig],
  );

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-start" shadow="md" width={360} withinPortal>
      <Popover.Target>
        <Button
          variant="subtle"
          size="xs"
          rightSection={catalog.isLoading ? <Loader size={12} /> : <IconChevronDown size={14} />}
          onClick={() => {
            if (!opened) clearDraftSelection();

            setOpened((current) => !current);
          }}
        >
          {providerLabel(selectedVoice)}
        </Button>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="sm">
          <Group justify="space-between" wrap="nowrap">
            <Text size="sm" fw={600}>
              Flow Voice
            </Text>
            <Text size="xs" c="dimmed">
              {getFlowVoiceProviderLabel(activeProvider)}
            </Text>
          </Group>

          {catalog.error && (
            <Alert color="yellow" icon={<IconAlertCircle size={16} />} p="xs">
              <Text size="xs">{catalog.error}</Text>
            </Alert>
          )}

          <Select
            size="xs"
            label="Provider"
            value={activeProvider}
            onChange={handleProviderChange}
            data={providerOptions}
            comboboxProps={nestedSelectComboboxProps}
          />

          {modelOptions.length > 0 && (
            <Select
              size="xs"
              label="Model"
              value={activeModelId}
              onChange={handleModelChange}
              data={modelOptions}
              comboboxProps={nestedSelectComboboxProps}
            />
          )}

          {activeVoice ? (
            <Group gap="xs" align="flex-end" wrap="nowrap">
              <Select
                size="xs"
                label="Voice"
                value={activeVoice.key}
                onChange={handleVoiceChange}
                data={visibleVoices.map((v) => ({ value: v.key, label: voiceLabel(v) }))}
                searchable
                style={{ flex: 1 }}
                comboboxProps={{ ...nestedSelectComboboxProps, width: 320 }}
              />
              {activeVoice.previewUrl && (
                <ActionIcon
                  variant="light"
                  color={preview.isPlaying ? 'red' : 'blue'}
                  size="sm"
                  onClick={preview.toggle}
                  title={preview.isPlaying ? 'Stop preview' : 'Preview voice'}
                  mb={2}
                >
                  {preview.isPlaying ? <IconPlayerStop size={14} /> : <IconPlayerPlay size={14} />}
                </ActionIcon>
              )}
            </Group>
          ) : (
            <Text size="xs" c="dimmed">
              No voices are currently available for this provider.
            </Text>
          )}

          {activeVoice?.voiceIdentifier === 'alice' && (
            <Select
              size="xs"
              label="Language"
              value={voiceConfig.language ?? 'en-US'}
              onChange={(value) => onChange({ ...voiceConfig, language: value ?? 'en-US' })}
              data={languageOptions}
              comboboxProps={nestedSelectComboboxProps}
            />
          )}

          <Group justify="flex-end">
            <Button size="xs" variant="subtle" onClick={() => setOpened(false)}>
              Close
            </Button>
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
